import { mainLog } from './logger.js';
import config from './config.js';
import consent from './consent.js';
import eventQueue from './eventQueue.js';

const SDK_GLOBAL_NAME = 'brweb';

const isReady = () => typeof window[SDK_GLOBAL_NAME]?.track === 'function';

const _shouldProcess = (action, payload) => {
  const cfg = config.getConfig();

  if (!cfg.enabled) {
    mainLog('[trackLayer:SDKAdapter] trackLayer disabled');
    return false;
  }

  if (!consent.hasConsent()) {
    mainLog(`[trackLayer:SDKAdapter] No consent - skipping: ${action}`);
    return false;
  }

  if (!isReady()) {
    mainLog(`[trackLayer:SDKAdapter] SDK not ready - queueing: ${action}`);
    eventQueue.enqueue(action, payload);
    return false;
  }

  return true;
};

const identify = (properties = {}) => {
  if (!_shouldProcess('identify', properties)) return;

  mainLog('[trackLayer:SDKAdapter] Identifying user', properties);
  window[SDK_GLOBAL_NAME].identify(properties);
};

const anonymize = (properties = {}) => {
  if (!_shouldProcess('anonymize', properties)) return;

  mainLog('[trackLayer:SDKAdapter] Anonymize user', properties);
  window[SDK_GLOBAL_NAME].anonymize();
};

const track = (eventType, properties = {}) => {
  if (!_shouldProcess(eventType, properties)) return;

  mainLog(`[trackLayer:SDKAdapter] Tracking event: ${eventType}`, properties);
  window[SDK_GLOBAL_NAME].track(eventType, properties);
};

const sendConsent = (eventType, properties = {}) => {
  if (!_shouldProcess(eventType, properties)) return;

  mainLog(`[trackLayer:SDKAdapter] Consent: ${eventType}`, properties);

  const eventsMap = prepareConsents(properties);


    for (const [eventType, properties] of Object.entries(eventsMap)) {
      mainLog(`[trackLayer:SDKAdapter] Consent: ${eventType}`, properties);
      if (properties.action) {
        window[SDK_GLOBAL_NAME].track('consent', properties);
      }
    }
};

const prepareConsents = (properties = {}) => {
  // 1. Agrupar las propiedades por evento/categoría
  const eventsMap = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined || value === '') continue;

    // Buscar dónde empieza el sufijo del evento (ej: 'action_abandoned_cart' -> prop: 'action', eventType: 'abandoned_cart')
    const knownProps = ['action', 'category', 'valid_until', 'timestamp'];
    const propMatch = knownProps.find(prop => key.startsWith(`${prop}_`));

    if (propMatch) {
      const propName = propMatch;
      const eventType = key.slice(propMatch.length + 1); // Extrae el nombre del evento (ej: 'abandoned_cart')

      if (!eventsMap[eventType]) {
        eventsMap[eventType] = {};
      }
      eventsMap[eventType][propName] = value;
    }
  }

  return Object.values(eventsMap);
};

const sendToServer = (eventType, payload) => {
  const cfg = config.getConfig();

  if (!cfg.apiUrl) {
    mainLog('Server API URL not configured');
    return Promise.reject(new Error('API URL not configured'));
  }

  return fetch(`${cfg.apiUrl}/api/track/${eventType}`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(payload),
  }).then((res) => res.json());
};

const sdkAdapter = {
  isReady,
  track,
  identify,
  anonymize,
  sendToServer,
  sendConsent,
  prepareConsents
};

export default sdkAdapter;